import { ChevronLeft, ChevronRight } from "lucide-react";

// Shared default for the lender and lender-user tables.
export const DEFAULT_TABLE_PAGE_SIZE = 40;

interface TablePaginationProps {
  totalRows: number;
  pageIndex: number;
  pageSize: number;
  onPageChange: (pageIndex: number) => void;
  disabled?: boolean;
}

export default function TablePagination({
  totalRows,
  pageIndex,
  pageSize,
  onPageChange,
  disabled = false,
}: TablePaginationProps) {
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const currentPage = pageIndex + 1;
  const firstResult = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const lastResult = Math.min((pageIndex + 1) * pageSize, totalRows);
  const canGoBack = pageIndex > 0 && !disabled;
  const canGoForward = pageIndex + 1 < pageCount && !disabled;

  const pageItems: Array<number | "ellipsis-start" | "ellipsis-end"> =
    pageCount <= 4
      ? Array.from({ length: pageCount }, (_, index) => index + 1)
      : currentPage <= 3
      ? [1, 2, 3, "ellipsis-end", pageCount]
      : currentPage >= pageCount - 2
      ? [1, "ellipsis-start", pageCount - 2, pageCount - 1, pageCount]
      : [1, "ellipsis-start", currentPage, "ellipsis-end", pageCount];

  return (
    <nav
      aria-label="Table pagination"
      className="flex flex-wrap items-center justify-between gap-3 px-1 py-5"
    >
      <p className="text-xs text-content-secondary">
        Showing {firstResult}-{lastResult} of {totalRows} results
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Previous page"
          onClick={() => onPageChange(Math.max(0, pageIndex - 1))}
          disabled={!canGoBack}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border-subtle bg-surface text-content-secondary transition-colors hover:border-brand hover:text-brand focus:outline-none focus:ring-2 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>

        {pageItems.map((item) =>
          typeof item === "number" ? (
            <button
              key={item}
              type="button"
              aria-label={`Page ${item}`}
              aria-current={item === currentPage ? "page" : undefined}
              onClick={() => onPageChange(item - 1)}
              disabled={disabled}
              className={`inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-40 ${
                item === currentPage
                  ? "border-brand bg-brand text-white"
                  : "border-border-subtle bg-surface text-content-secondary hover:border-brand hover:text-brand"
              }`}
            >
              {item}
            </button>
          ) : (
            <span
              key={item}
              aria-hidden="true"
              className="inline-flex h-8 min-w-8 items-center justify-center text-xs text-content-secondary"
            >
              &hellip;
            </span>
          )
        )}

        <button
          type="button"
          aria-label="Next page"
          onClick={() => onPageChange(Math.min(pageCount - 1, pageIndex + 1))}
          disabled={!canGoForward}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border-subtle bg-surface text-content-secondary transition-colors hover:border-brand hover:text-brand focus:outline-none focus:ring-2 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}
